import React, { Fragment } from 'react';
import { useQuery } from '@apollo/client';
import { ChecklistDark } from '@otp/icons';
import { useTranslation } from 'react-i18next';
import Grid from '@mui/material/Grid';
import { Card, Typography } from '@otp/ui';
import { Routes } from 'src/components/common/router/constants';
import { useNavigate } from 'react-router-dom';
import {
  type User,
  WttpQuestionnaireResponsesCountDocument,
  WttpQuestionnaireResponsesCountQuery,
} from 'src/graphql/generated/graphql';
import { useQuestionnaireResponses } from 'src/hooks/query/use-questionnaire-responses';
import { useQuestionnaireResponsesStats } from 'src/hooks/query/use-questionnaire-responses-stats';
import { getFullName } from 'src/utils/user-data';
import { useAuthContext } from 'src/context/auth.context';
import { getWttpResponseFiltersBasedOnUser } from 'src/components/wttp/questionnaire-responses/list/utils';

export const CurrentAssignments = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin, isOtpPro, user } = useAuthContext();

  const { wttpQuestionnaireResponses, loading } = useQuestionnaireResponses({
    filter: {
      isActive: true,
      ...getWttpResponseFiltersBasedOnUser(user as User, {
        isAdmin,
        isOtpPro,
      }),
    },
    pagination: {
      limit: 4,
      offset: 0,
    },
  });

  const { data: activeWttpData } =
    useQuery<WttpQuestionnaireResponsesCountQuery>(
      WttpQuestionnaireResponsesCountDocument,
      {
        variables: {
          filter: {
            isActive: true,
            ...getWttpResponseFiltersBasedOnUser(user as User, {
              isAdmin,
              isOtpPro,
            }),
          },
          pagination: {
            limit: 0,
          },
        },
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-only',
      },
    );
  const activeWttpCount = activeWttpData?.wttpQuestionnaireResponsesCount;

  const { over30Days } = useQuestionnaireResponsesStats({
    filter: {
      isActive: false,
    },
  });

  return (
    <Grid item xs={12}>
      <Card className="bg-[#F8F9FD] border-none h-full w-full">
        <Grid container spacing={3}>
          {/* Stats Section */}
          <Grid item xs={12}>
            <Card
              isLink
              onClick={() => navigate(Routes.wttpQuestionnaireResponses.path)}
              className="w-full cursor-pointer hover:bg-gray-100 transition-colors py-4 px-6 rounded-3xl bg-[#E7EEFF] border-none"
            >
              <Grid container spacing={4}>
                <Grid
                  item
                  xs={4}
                  container
                  direction="column"
                  spacing={1}
                  gap={1}
                >
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    className="text-lg md:text-xl lg:text-2xl leading-tight"
                    fontFamily="Inter"
                  >
                    {activeWttpCount || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-gray-600 text-xs md:text-sm"
                    fontFamily="Inter"
                  >
                    {t('my-organisation.wttp.current-assignments.assignments')}
                  </Typography>
                </Grid>

                <Grid item xs={1} container justifyContent="center">
                  <div className="w-px h-[100%] bg-gray-300" />
                </Grid>

                <Grid
                  item
                  container
                  xs={6}
                  direction="column"
                  spacing={1}
                  gap={1}
                >
                  <Typography
                    variant="h4"
                    fontWeight="bold"
                    className="text-red-500 text-lg md:text-xl lg:text-2xl leading-tight"
                    fontFamily="Inter"
                  >
                    {over30Days || 0}
                  </Typography>
                  <Typography
                    variant="body2"
                    className="text-red-500 text-xs md:text-sm"
                    fontFamily="Inter"
                  >
                    {t(
                      'my-organisation.wttp.current-assignments.missed-in-last-30-days',
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </Card>
          </Grid>

          {/* List Section */}
          <Grid container item xs={12} gap={2}>
            {wttpQuestionnaireResponses.length > 0 ? (
              wttpQuestionnaireResponses.map((wttpQuestionnaireResponse) => (
                <Fragment key={wttpQuestionnaireResponse?.id}>
                  <Grid container alignItems="center">
                    <Grid xs={10}>
                      <Typography variant="body1" fontWeight="bold">
                        {wttpQuestionnaireResponse?.questionnaire?.name}
                      </Typography>
                      <Typography variant="body2" className="text-gray-600">
                        {getFullName(wttpQuestionnaireResponse.assignedTo)
                          ? `${t('my-organisation.wttp.assigned-to')} ${getFullName(wttpQuestionnaireResponse.assignedTo)}`
                          : t('my-organisation.wttp.not-assigned')}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} container justifyContent="flex-end">
                      <Typography
                        variant="body2"
                        className="text-white bg-blue-500 px-3 py-1 rounded-full"
                      >
                        {wttpQuestionnaireResponse?.store?.nationalStoreNumber}
                      </Typography>
                    </Grid>
                  </Grid>
                  <hr className="w-full h-[1px] bg-blue-300/40 border-0 my-0.5" />
                </Fragment>
              ))
            ) : (
              <Grid
                container
                justifyContent="center"
                alignItems="center"
                className="py-4"
              >
                <Typography variant="body2" color="text.secondary">
                  {t('common.no-data-available', 'No data available')}
                </Typography>
              </Grid>
            )}
          </Grid>
        </Grid>
      </Card>
    </Grid>
  );
};
